import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetLastIpdRefund, StoreIpdRefund } from "../../../../api/ipd/ipdRefundAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { GetLastEntry } from "../../../../api/common/common";

// Validation schema (basic example — you can expand this)
const schema = yup.object().shape({
  ipdEntryId: yup.string().required("IPD No is required"),
  date: yup.date().required("Date is required"),
  paymentMode: yup.string().required("Payment Mode is required"),
  amount: yup
    .number()
    .typeError("Amount must be a number")
    .required("Amount is required"),
});

const AddIpdRefundEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [ipdEntries, setIpdEntries] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors } } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        type: "IPD",
      }
    });

  const fetchIpdEntries = async () => {
    try {
      const response = await GetIpdEntries();
      if (response.success) {
        setIpdEntries(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchLastEntry = async () => {
      try {
        const response = await GetLastEntry();
        if (response.success) {
          setValue(
            "docNo",
            String(response?.data?.lastIpdRefundEntry?.docNo ? response.data.lastIpdRefundEntry.docNo + 1 : 1)
          );
        } else {
          showToast("error", response.message);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };

  useEffect(() => {
    fetchIpdEntries();
    fetchLastEntry();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const response = await StoreIpdRefund(data);

      if (response.success) {
        showToast("success", response.message);
        navigate(`/ipd/transaction/ipd-refund-entry/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="IPD Refund" title="Add IPD Refund Entry" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add IPD Refund Entry</h5>
                <Link
                  to={`/ipd/transaction/ipd-refund-entry/list`}
                >
                  <button className="btn btn-primary" id="list">
                    List IPD Refund Entry
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List IPD Refund Entry
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* Name */}
                    <div className="col-6">
                      <div className="form-group">
                        <label>Type</label>
                        <input
                          className="form-control"
                          {...register("type")}
                          placeholder="Type"
                          readOnly
                        />
                        {errors.type &&
                          <p className="text-danger">
                            {errors.type.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          className="form-control"
                          defaultValue={new Date().toISOString().slice(0, 10)}
                          {...register("date")}
                          placeholder="Date"
                        />
                        {errors.date &&
                          <p className="text-danger">
                            {errors.date.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Ipd No</label>
                        <select
                          className="form-control"
                          {...register("ipdEntryId", {
                            onChange: (e) => {
                              setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                            }
                          })}
                        >
                          <option value="">Select Ipd No</option>
                          {ipdEntries.map(ipd =>
                            <option key={ipd._id} value={ipd._id}>
                              {ipd.ipdNo}
                            </option>
                          )}
                        </select>
                        {errors.ipdEntryId &&
                          <p className="text-danger">
                            {errors.ipdEntryId.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Doc No</label>
                        <input
                          className="form-control"
                          {...register("docNo")}
                          placeholder="Doc No"
                          readOnly
                        />
                        {errors.docNo &&
                          <p className="text-danger">
                            {errors.docNo.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Patient Details
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>UHID No</label>
                        <input
                          className="form-control"
                          placeholder="UHID No"
                          value={patientDetails?.patientId?.uhidNo}
                          readOnly
                        />
                      </div>
                    </div>
                    
                    <div className="col-6">
                      <div className="form-group">
                        <label>Patient</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.patientName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>guardian Name</label>
                        <input
                          className="form-control"
                          placeholder="guardian Name"
                          value={patientDetails?.patientId?.guardianName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Age</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.age}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Case</label>
                        <input
                          className="form-control"
                          placeholder="Case"
                          value={patientDetails?.caseId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Consultant Doctor</label>
                        <input
                          className="form-control"
                          placeholder="Consultant Doctor"
                          value={patientDetails?.consultantDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>ReferenceDoctor</label>
                        <input
                          className="form-control"
                          placeholder="ReferenceDoctor"
                          value={patientDetails?.referenceDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          className="form-control"
                          placeholder="Address"
                          value={patientDetails?.patientId?.address}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">

                    <div className="col-6">
                      <div className="form-group">
                        <label>Payment Mode</label>
                        <select
                          className="form-control"
                          {...register("paymentMode")}
                          placeholder="Payment Mode"
                        >
                          <option value="Cash">Cash</option>
                          <option value="Online">Online</option>
                        </select>
                        {errors.paymentMode &&
                          <p className="text-danger">
                            {errors.paymentMode.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register("amount")}
                          placeholder="Amount"
                        />
                        {errors.amount &&
                          <p className="text-danger">
                            {errors.amount.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Debit Account</label>
                        <input
                          className="form-control"
                          {...register("debitAccount")}
                          placeholder="Debit Account"
                        />
                        {errors.debitAccount &&
                          <p className="text-danger">
                            {errors.debitAccount.message}
                          </p>}
                      </div>
                    </div>
                    
                    <div className="col-6">
                      <div className="form-group">
                        <label>Remark</label>
                        <input
                          className="form-control"
                          {...register("remark")}
                          placeholder="Remark"
                        />
                        {errors.remark &&
                          <p className="text-danger">
                            {errors.remark.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddIpdRefundEntry;
